import React, { useState } from "react";
import { toast } from "sonner";
import { apiErr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useCustomer } from "@/context/CustomerContext";
import { setToken } from "@/lib/customerApi";
import { normMobile } from "@/lib/personValidation";
import { Button, Card, Field, inputCls, inputStyle } from "./AccountUI";

const pwErr = (v) => (v.length < 8 ? "Use at least 8 characters." : !/[A-Za-z]/.test(v) || !/\d/.test(v) ? "Include a letter and a number." : "");

export default function AccountProfile() {
  const s = useStore();
  const cu = useCustomer();
  const c = cu.customer;
  const [f, setF] = useState({ name: c.name, mobile: c.mobile });
  const [errs, setErrs] = useState({});
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "" });
  const [pwErrs, setPwErrs] = useState({});
  const [pwBusy, setPwBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 2 || !/\p{L}/u.test(f.name)) er.name = "Enter your name.";
    if (!/^[6-9]\d{9}$/.test(normMobile(f.mobile))) er.mobile = "Enter a valid 10-digit mobile number.";
    setErrs(er);
    if (Object.keys(er).length) return;
    setSaving(true);
    try { const { data } = await cu.api.put("/me", f); cu.setCustomer(data); setF({ name: data.name, mobile: data.mobile }); toast.success("Profile updated."); }
    catch (err) { const m = apiErr(err.response?.data?.detail); if (err.response?.status === 409) setErrs({ mobile: m }); else toast.error(m); }
    finally { setSaving(false); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    const er = {};
    if (!pw.current) er.current = "Enter your current password.";
    if (pwErr(pw.next)) er.next = pwErr(pw.next);
    setPwErrs(er);
    if (Object.keys(er).length) return;
    setPwBusy(true);
    try { const { data } = await cu.api.post("/me/password", { current_password: pw.current, new_password: pw.next }); setToken(s.slug, data.token); setPw({ current: "", next: "" }); toast.success("Password changed."); }
    catch (err) { const m = apiErr(err.response?.data?.detail); if (err.response?.status === 400) setPwErrs({ current: m }); else toast.error(m); }
    finally { setPwBusy(false); }
  };

  return (
    <div data-testid="account-profile">
      <h2 className="text-3xl font-extrabold" style={{ color: s.c.primary }}>My profile</h2>
      <p className="mt-1" style={{ color: s.muted }}>Your details are used to pre-fill checkout.</p>
      <div className="grid lg:grid-cols-2 gap-5 mt-6">
        <Card s={s} className="p-6">
          <h3 className="font-bold mb-4">Personal details</h3>
          <form onSubmit={save} className="space-y-4" noValidate>
            <Field s={s} label="Full name" error={errs.name}><input value={f.name} maxLength={80} onChange={(e) => { setF({ ...f, name: e.target.value }); setErrs({ ...errs, name: undefined }); }} data-testid="acct-profile-name" className={inputCls} style={inputStyle(s, errs.name)} /></Field>
            <Field s={s} label="Mobile" error={errs.mobile}><input value={f.mobile} maxLength={16} inputMode="numeric" onChange={(e) => { setF({ ...f, mobile: e.target.value }); setErrs({ ...errs, mobile: undefined }); }} data-testid="acct-profile-mobile" className={inputCls} style={inputStyle(s, errs.mobile)} /></Field>
            <Field s={s} label="Email" hint="Your email is your login and can't be changed here."><input value={c.email} disabled className={inputCls} style={{ ...inputStyle(s), opacity: 0.7 }} /></Field>
            <Button s={s} type="submit" disabled={saving} data-testid="acct-profile-save">{saving ? "Saving…" : "Save changes"}</Button>
          </form>
        </Card>
        <Card s={s} className="p-6">
          <h3 className="font-bold mb-4">Change password</h3>
          <form onSubmit={changePw} className="space-y-4" noValidate>
            <Field s={s} label="Current password" error={pwErrs.current}><input type="password" autoComplete="current-password" value={pw.current} onChange={(e) => { setPw({ ...pw, current: e.target.value }); setPwErrs({ ...pwErrs, current: undefined }); }} data-testid="acct-pw-current" className={inputCls} style={inputStyle(s, pwErrs.current)} /></Field>
            <Field s={s} label="New password" error={pwErrs.next} hint="At least 8 characters, with a letter and a number."><input type="password" autoComplete="new-password" maxLength={72} value={pw.next} onChange={(e) => { setPw({ ...pw, next: e.target.value }); setPwErrs({ ...pwErrs, next: undefined }); }} data-testid="acct-pw-new" className={inputCls} style={inputStyle(s, pwErrs.next)} /></Field>
            <Button s={s} type="submit" disabled={pwBusy} data-testid="acct-pw-save">{pwBusy ? "Updating…" : "Update password"}</Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
